// v3.15-1203 REPORT-001 — Compensation Reports (read-only).
//
// Unified reports surface. Reads from immutable records. Never calculates
// or modifies compensation.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, BarChart3, Filter, Download } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const TABS = [
  { key: 'pending',        label: 'Pending' },
  { key: 'paid',           label: 'Paid' },
  { key: 'ytd',            label: 'YTD Earnings' },
  { key: 'lifetime',       label: 'Lifetime Earnings' },
  { key: 'by-employee',    label: 'By Employee' },
  { key: 'by-company',     label: 'By Company' },
  { key: 'by-role',        label: 'By Role' },
  { key: 'payroll-history',label: 'Payroll History' },
];

const fmtMoney = (n) =>
  Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date((iso.length === 10 ? iso : iso.slice(0, 10)) + 'T00:00:00')
      .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
};

export default function CompensationReportsPage() {
  const [tab, setTab]           = useState('pending');
  const [loading, setLoading]   = useState(false);
  const [data, setData]         = useState(null);
  const [error, setError]       = useState(null);

  // Filters
  const [companyId, setCompanyId]     = useState('');
  const [employeeId, setEmployeeId]   = useState('');
  const [role, setRole]               = useState('');
  const [planId, setPlanId]           = useState('');
  const [fromDate, setFromDate]       = useState('');
  const [toDate, setToDate]           = useState('');

  const fetchReport = useCallback(async () => {
    setLoading(true); setError(null); setData(null);
    try {
      const params = {};
      if (companyId)  params.company_id            = companyId;
      if (employeeId) params.employee_id           = employeeId;
      if (role)       params.role                  = role;
      if (planId)     params.compensation_plan_id  = planId;
      if (fromDate)   params.from_date             = fromDate;
      if (toDate)     params.to_date               = toDate;
      const { data } = await api.get(`/comp-reports/${tab}`, { params });
      setData(data);
    } catch (e) {
      if (e?.response?.status === 403) setError('You do not have permission for this report.');
      else toast.error('Failed to load report');
    } finally { setLoading(false); }
  }, [tab, companyId, employeeId, role, planId, fromDate, toDate]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const downloadCsv = () => {
    if (!data?.items?.length) { toast.error('Nothing to export'); return; }
    const rows = data.items;
    const cols = Object.keys(rows[0]);
    const csv = [
      cols.join(','),
      ...rows.map((r) => cols.map((c) => JSON.stringify(r[c] ?? '')).join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `comp-report-${tab}-${Date.now()}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const summary = useMemo(() => {
    if (!data) return null;
    if (data.total_amount != null) {
      return { label: 'Total', value: fmtMoney(data.total_amount) };
    }
    if (data.totals) {
      return {
        label: 'Totals',
        value: `Pending ${fmtMoney(data.totals.pending)} · Paid ${fmtMoney(data.totals.paid)} · Total ${fmtMoney(data.totals.total)}`,
      };
    }
    return { label: 'Count', value: data.count ?? 0 };
  }, [data]);

  const renderRows = () => {
    if (!data?.items?.length) {
      return <div className="p-10 text-center text-slate-500" data-testid="report-empty">No data for the selected filters.</div>;
    }
    const rows = data.items;
    if (tab === 'pending' || tab === 'paid') {
      return (
        <table className="w-full text-sm" data-testid="report-table">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
            <tr>
              <th className="text-left px-3 py-2">Date</th>
              <th className="text-left px-3 py-2">Employee</th>
              <th className="text-left px-3 py-2">Company</th>
              <th className="text-left px-3 py-2">Role</th>
              <th className="text-left px-3 py-2">Deal</th>
              <th className="text-left px-3 py-2">Plan</th>
              <th className="text-right px-3 py-2">Amount</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.compensation_event_id || r.id || i} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-3 py-2 whitespace-nowrap">{fmtDate(r.event_date || r.payroll_date)}</td>
                <td className="px-3 py-2">{r.employee_name || r.employee_id}</td>
                <td className="px-3 py-2">{r.company_name || r.company_id || '—'}</td>
                <td className="px-3 py-2 capitalize">{(r.role || '').replaceAll('_', ' ')}</td>
                <td className="px-3 py-2">{r.deal_name || r.deal_id || '—'}</td>
                <td className="px-3 py-2 text-xs">{r.compensation_plan_code || '—'} <span className="text-slate-400">v{r.compensation_plan_version || '—'}</span></td>
                <td className="px-3 py-2 text-right font-semibold">{fmtMoney(r.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
    if (tab === 'ytd' || tab === 'lifetime') {
      const hubKey  = tab === 'ytd' ? 'hub_ytd'      : 'hub_lifetime';
      const openKey = tab === 'ytd' ? 'opening_ytd'  : 'opening_lifetime';
      const totKey  = tab === 'ytd' ? 'total_ytd'    : 'total_lifetime';
      return (
        <table className="w-full text-sm" data-testid="report-table">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
            <tr>
              <th className="text-left px-3 py-2">Employee</th>
              <th className="text-left px-3 py-2">Company</th>
              <th className="text-right px-3 py-2">Opening</th>
              <th className="text-right px-3 py-2">Hub</th>
              <th className="text-right px-3 py-2">Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.employee_id || i} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-3 py-2 font-semibold">{r.employee_name || r.employee_id}</td>
                <td className="px-3 py-2">{r.company_name || '—'}</td>
                <td className="px-3 py-2 text-right">{fmtMoney(r[openKey])}</td>
                <td className="px-3 py-2 text-right">{fmtMoney(r[hubKey])}</td>
                <td className="px-3 py-2 text-right font-bold text-blue-700">{fmtMoney(r[totKey])}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
    if (tab.startsWith('by-')) {
      return (
        <table className="w-full text-sm" data-testid="report-table">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
            <tr>
              <th className="text-left px-3 py-2">{tab === 'by-employee' ? 'Employee' : tab === 'by-company' ? 'Company' : 'Role'}</th>
              <th className="text-right px-3 py-2">Pending</th>
              <th className="text-right px-3 py-2">Paid</th>
              <th className="text-right px-3 py-2">Total</th>
              <th className="text-right px-3 py-2">Entries</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.key || i} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-3 py-2 font-semibold capitalize">{(r.label || '').toString().replaceAll('_', ' ')}</td>
                <td className="px-3 py-2 text-right text-amber-700">{fmtMoney(r.pending)}</td>
                <td className="px-3 py-2 text-right text-emerald-700">{fmtMoney(r.paid)}</td>
                <td className="px-3 py-2 text-right font-bold text-blue-700">{fmtMoney(r.total)}</td>
                <td className="px-3 py-2 text-right text-xs text-slate-500">{(r.pending_count || 0) + (r.paid_count || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
    // payroll-history
    return (
      <table className="w-full text-sm" data-testid="report-table">
        <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
          <tr>
            <th className="text-left px-3 py-2">Batch</th>
            <th className="text-left px-3 py-2">Payroll Date</th>
            <th className="text-left px-3 py-2">Processing</th>
            <th className="text-right px-3 py-2">Employees (scope)</th>
            <th className="text-right px-3 py-2">Entries (scope)</th>
            <th className="text-right px-3 py-2">Scoped Total</th>
            <th className="text-right px-3 py-2">Batch Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || i} className="border-t border-slate-100 hover:bg-slate-50">
              <td className="px-3 py-2 font-mono text-xs">{r.batch_id}</td>
              <td className="px-3 py-2 whitespace-nowrap">{fmtDate(r.payroll_date)}</td>
              <td className="px-3 py-2 whitespace-nowrap">{fmtDate(r.processing_date)}</td>
              <td className="px-3 py-2 text-right">{r.scoped_employee_count}</td>
              <td className="px-3 py-2 text-right">{r.scoped_entry_count}</td>
              <td className="px-3 py-2 text-right font-semibold text-emerald-700">{fmtMoney(r.scoped_total)}</td>
              <td className="px-3 py-2 text-right text-slate-500">{fmtMoney(r.total_amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div className="space-y-4" data-testid="comp-reports-page">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <BarChart3 className="w-6 h-6 text-blue-600" />
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900">Compensation Reports</h1>
            <p className="text-sm text-slate-500">
              Read-only reports drawn from immutable Compensation Events,
              Ledger, Payroll Transactions, and Opening Balances.
            </p>
          </div>
        </div>
        <Button data-testid="export-csv-btn" onClick={downloadCsv} disabled={!data?.items?.length}
                className="bg-slate-700 hover:bg-slate-800 text-white">
          <Download className="w-4 h-4 mr-2" /> Export CSV
        </Button>
      </div>

      {/* Filter bar */}
      <div className="bg-white p-4 rounded-2xl border border-slate-200 grid grid-cols-2 md:grid-cols-6 gap-3">
        <div>
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Company ID</label>
          <Input data-testid="filter-company" value={companyId} onChange={(e) => setCompanyId(e.target.value)} placeholder="UUID" />
        </div>
        <div>
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Employee ID</label>
          <Input data-testid="filter-employee" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} placeholder="UUID" />
        </div>
        <div>
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Role</label>
          <Input data-testid="filter-role" value={role} onChange={(e) => setRole(e.target.value)} placeholder="sales_rep_1" />
        </div>
        <div>
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Plan ID</label>
          <Input data-testid="filter-plan" value={planId} onChange={(e) => setPlanId(e.target.value)} placeholder="UUID" />
        </div>
        <div>
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">From</label>
          <Input data-testid="filter-from" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </div>
        <div>
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">To</label>
          <Input data-testid="filter-to" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap items-center gap-1 border-b border-slate-200">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={
              'px-3 py-2 text-sm font-semibold border-b-2 -mb-px transition ' +
              (tab === t.key
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-slate-500 hover:text-slate-800')
            }>{t.label}</button>
        ))}
      </div>

      {/* Summary */}
      {summary ? (
        <div className="bg-white p-3 rounded-xl border border-slate-200 text-sm">
          <span className="text-[11px] font-bold uppercase text-slate-500 mr-2">{summary.label}:</span>
          <span className="font-bold text-slate-800">{summary.value}</span>
        </div>
      ) : null}

      {/* Body */}
      {error ? (
        <div className="p-6 bg-rose-50 text-rose-700 border border-rose-200 rounded-xl" data-testid="report-forbidden">{error}</div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-x-auto">
          {loading ? (
            <div className="p-10 flex items-center justify-center text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
            </div>
          ) : renderRows()}
        </div>
      )}
    </div>
  );
}
